import { c as _c } from "react/compiler-runtime";
/**
 * Cinematic, image-led wizard renderers — two layouts ported from the v0
 * design exploration but rebased onto our design system (deep-sea-blue / cream
 * / sunshine-yellow tokens, our typography classes, next/image + Sanity assets)
 * and wired to the real concierge answers + submit flow.
 *
 *  - "timeline": one question per screen on a full-bleed harbour photo, content
 *    anchored to the bottom, glass option cards. Single-selects auto-advance.
 *  - "mosaic":   the choices ARE photographs — an image-tile grid on a dark
 *    ground. Single-selects auto-advance; multi-select confirms.
 *
 * Both consume the same props as the classic WizardBody, so they hook straight
 * into VariantConcierge's state machine (answers → next() → generatePlan).
 */
import { type ReactNode, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Cloud, CloudRain, CloudSnow, Sun, Wind } from "lucide-react";
import { Icon } from "@repo/ui/icon";
import { durationOptionsFor } from "@/lib/concierge/wizard-answer-map";
type DurationId = "1h" | "2-3h" | "half" | "full";
type GroupId = "solo" | "couple" | "family" | "friends";
type PaceId = "easy" | "balanced" | "packed";
type InterestId = "art" | "food" | "shop" | "nature" | "history" | "family" | "sundowner";
type BudgetId = "light" | "mid" | "spoil";
export type WizardLayout = "timeline" | "mosaic";
export interface CinematicAnswers {
  date: string | null;
  arrivalTime: string | null;
  duration: DurationId | null;
  group: GroupId | null;
  pace: PaceId | null;
  interests: InterestId[];
  budget: BudgetId | null;
}
interface StepDef {
  key: keyof CinematicAnswers;
  eyebrow: string;
  question: string;
  sub: string;
  kind: "single" | "multi" | "date" | "time";
  bg: string;
  options: {
    id: string;
    label: string;
    sub?: string;
    img?: string;
  }[];
}

// Quick date presets for the date step (local ISO YYYY-MM-DD).
function quickDatePicks(): {
  label: string;
  iso: string;
}[] {
  const iso = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const weekend = new Date(today);
  weekend.setDate(today.getDate() + (6 - today.getDay() + 7) % 7);
  return [{
    label: "Today",
    iso: iso(today)
  }, {
    label: "Tomorrow",
    iso: iso(tomorrow)
  }, {
    label: "This weekend",
    iso: iso(weekend)
  }];
}
function formatDateChip(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-ZA", {
    weekday: "short",
    day: "numeric",
    month: "short"
  });
}

/* -------------------------------------------------------------------------- */
/* Arrival time                                                               */
/* -------------------------------------------------------------------------- */

const ARRIVAL_BANDS: {
  label: string;
  times: string[];
}[] = [{
  label: "Morning",
  times: ["09:00", "10:00", "11:00"]
}, {
  label: "Afternoon",
  times: ["12:00", "13:00", "14:00", "15:00", "16:00"]
}, {
  label: "Evening",
  times: ["17:00", "18:00", "19:00", "20:00"]
}];
function formatArrival(time: string): string {
  const [h = 0, m = 0] = time.split(":").map(Number);
  const suffix = h >= 12 ? "pm" : "am";
  const hour = h % 12 || 12;
  return m ? `${hour}:${String(m).padStart(2, "0")}${suffix}` : `${hour}${suffix}`;
}
function whatsOpen(time: string): string {
  const hour = Number(time.slice(0, 2));
  if (hour < 11) return "Coffee spots are open and the shops and attractions are opening up. We'll start you with breakfast and ease into the day.";
  if (hour < 14) return "Everything's trading — attractions, shops and lunch spots. We'll build around lunch.";
  if (hour < 17) return "Plenty of afternoon left. We'll only suggest places that are still open when you get to them.";
  if (hour < 19) return "The shops are still open and it's nearly sundowner time — we'll lean into drinks and dinner.";
  return "An evening visit — we'll pick restaurants, bars and the spots that trade late.";
}
function capeTownNow(): {
  iso: string;
  minutes: number;
} {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Africa/Johannesburg",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).formatToParts(new Date());
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? "";
  return {
    iso: `${get("year")}-${get("month")}-${get("day")}`,
    minutes: Number(get("hour")) * 60 + Number(get("minute"))
  };
}

/** Arrival times already gone when the visit is today in Cape Town. */
function pastArrivals(date: string): Set<string> {
  const now = capeTownNow();
  if (date !== now.iso) return new Set();
  return new Set(ARRIVAL_BANDS.flatMap(b => b.times).filter(t => {
    const [h = 0, m = 0] = t.split(":").map(Number);
    return h * 60 + m <= now.minutes - 30;
  }));
}
function ArrivalTimeChoice(t0) {
  const $ = _c(21);
  const {
    dateValue,
    value,
    onPick
  } = t0;
  let chip;
  let past;
  let t1;
  let t2;
  if ($[0] !== dateValue || $[1] !== onPick || $[2] !== value) {
    past = pastArrivals(dateValue);
    chip = _temp;
    t1 = "flex max-w-xl flex-col gap-5";
    t2 = ARRIVAL_BANDS.map(band => <div key={band.label} role="group" aria-label={band.label}><p className="mb-2.5 text-[10px] font-semibold uppercase tracking-[0.28em] text-cream-100/55">{band.label}</p><div className="flex flex-wrap gap-2">{band.times.map(t => <button key={t} type="button" aria-pressed={value === t} disabled={past.has(t)} onClick={() => onPick(t)} className={chip(value === t)}>{formatArrival(t)}</button>)}</div></div>);
    $[0] = dateValue;
    $[1] = onPick;
    $[2] = value;
    $[3] = chip;
    $[4] = past;
    $[5] = t1;
    $[6] = t2;
  } else {
    chip = $[3];
    past = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const t3 = value === "flexible";
  let t4;
  if ($[7] !== onPick) {
    t4 = () => onPick("flexible");
    $[7] = onPick;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = chip(value === "flexible");
  let t6;
  if ($[9] !== t3 || $[10] !== t4 || $[11] !== t5) {
    t6 = <div><button type="button" aria-pressed={t3} onClick={t4} className={t5}>Not sure yet</button></div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Icon name="schedule" className="size-6 shrink-0 text-sunshine-yellow-400" />;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = value && value !== "flexible" ? `Arriving at ${formatArrival(value)}. ${whatsOpen(value)}` : value === "flexible" ? "No problem \u2014 we'll plan around the usual opening hours." : past.size > 0 ? "Earlier times have passed for today. Pick when you'll get here and we'll only suggest what's open." : "Pick when you'll get here and we'll only suggest what's open.";
  let t9;
  if ($[14] !== t8) {
    t9 = <div aria-live="polite" aria-atomic={true} className="flex min-h-[84px] items-center gap-3 rounded-2xl border border-cream-100/15 bg-deep-sea-blue-950/55 px-4 py-3 backdrop-blur">{t7}<p className="text-xs leading-relaxed text-cream-100/75">{t8}</p></div>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t1 || $[17] !== t2 || $[18] !== t6 || $[19] !== t9) {
    t10 = <div className={t1}>{t2}{t6}{t9}</div>;
    $[16] = t1;
    $[17] = t2;
    $[18] = t6;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}

/* -------------------------------------------------------------------------- */
/* Weather widget — live forecast for the selected day                        */
/* -------------------------------------------------------------------------- */

// V&A Waterfront, Cape Town.
function _temp(active) {
  return `rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] backdrop-blur transition-all active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-35 ${active ? "border-sunshine-yellow-400 bg-sunshine-yellow-400 text-deep-sea-blue-950" : "border-cream-100/40 bg-deep-sea-blue-900/55 text-cream-100 hover:border-cream-100/60"}`;
}
const WEATHER_LAT = -33.9036;
const WEATHER_LNG = 18.4197;
type WeatherState = {
  status: "idle";
} | {
  status: "loading";
} | {
  status: "ok";
  max: number;
  min: number;
  code: number;
  wind: number;
  pop: number;
} | {
  status: "unavailable";
};

// Cache results per date so hovering the day rail doesn't re-hit the API.
const weatherCache = new Map<string, WeatherState>();
function weatherInfo(code: number): {
  label: string;
  Icon: typeof Sun;
} {
  if (code === 0) return {
    label: "Clear sky",
    Icon: Sun
  };
  if (code === 1) return {
    label: "Mostly sunny",
    Icon: Sun
  };
  if (code === 2) return {
    label: "Partly cloudy",
    Icon: Cloud
  };
  if (code === 3) return {
    label: "Overcast",
    Icon: Cloud
  };
  if (code === 45 || code === 48) return {
    label: "Fog",
    Icon: Cloud
  };
  if (code >= 51 && code <= 67) return {
    label: "Drizzle",
    Icon: CloudRain
  };
  if (code >= 71 && code <= 77) return {
    label: "Snow",
    Icon: CloudSnow
  };
  if (code >= 80 && code <= 82) return {
    label: "Rain showers",
    Icon: CloudRain
  };
  if (code >= 85 && code <= 86) return {
    label: "Snow showers",
    Icon: CloudSnow
  };
  if (code >= 95) return {
    label: "Thunderstorm",
    Icon: Wind
  };
  return {
    label: "Mixed",
    Icon: Cloud
  };
}
function weatherDateLabel(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-ZA", {
    weekday: "short",
    day: "numeric",
    month: "short"
  });
}

/**
 * Live Cape Town forecast for `date`. Render keyed by date (`key={date}`) so a
 * new day remounts and reads the cache instantly; uncached days fetch once.
 */
export function WeatherWidget(t0) {
  const $ = _c(46);
  const {
    date
  } = t0;
  let t1;
  if ($[0] !== date) {
    t1 = !!date && date !== "flexible" && /^\d{4}-\d{2}-\d{2}$/.test(date);
    $[0] = date;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const valid = t1;
  let t2;
  if ($[2] !== date || $[3] !== valid) {
    t2 = () => valid ? weatherCache.get(date) ?? {
      status: "loading"
    } : {
      status: "idle"
    };
    $[2] = date;
    $[3] = valid;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const [state, setState] = useState(t2);
  let t3;
  let t4;
  if ($[5] !== date || $[6] !== valid) {
    t3 = () => {
      if (!valid || weatherCache.get(date)?.status === "ok") {
        return;
      }
      let active = true;
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${WEATHER_LAT}&longitude=${WEATHER_LNG}&daily=weather_code,temperature_2m_max,temperature_2m_min,wind_speed_10m_max,precipitation_probability_max&timezone=Africa%2FJohannesburg&start_date=${date}&end_date=${date}`;
      fetch(url).then(_temp2).then(d => {
        const daily = d?.daily;
        const max = daily?.temperature_2m_max?.[0];
        const min = daily?.temperature_2m_min?.[0];
        const code = daily?.weather_code?.[0];
        let next;
        if (typeof max === "number" && typeof min === "number" && typeof code === "number") {
          next = {
            status: "ok",
            max: Math.round(max),
            min: Math.round(min),
            code,
            wind: Math.round(daily?.wind_speed_10m_max?.[0] ?? 0),
            pop: Math.round(daily?.precipitation_probability_max?.[0] ?? 0)
          };
        } else {
          next = {
            status: "unavailable"
          };
        }
        weatherCache.set(date, next);
        if (active) {
          setState(next);
        }
      }).catch(() => {
        if (active) {
          setState({
            status: "unavailable"
          });
        }
      });
      return () => {
        active = false;
      };
    };
    t4 = [valid, date];
    $[5] = date;
    $[6] = valid;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      "aria-live": "polite",
      "aria-atomic": true
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const liveProps = t5 as const;
  if (state.status === "idle") {
    let t6;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="flex min-h-[84px] items-center gap-3 rounded-2xl border border-cream-100/15 bg-deep-sea-blue-950/55 px-4 py-3 backdrop-blur" {...liveProps}><Cloud size={18} aria-hidden="true" className="text-cream-100/40" /><p className="text-xs text-cream-100/70">Pick a day to see the harbour forecast.</p></div>;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    return t6;
  }
  if (state.status === "loading") {
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="flex min-h-[84px] items-center gap-3 rounded-2xl border border-cream-100/15 bg-deep-sea-blue-950/55 px-4 py-3 backdrop-blur" {...liveProps}><Cloud size={18} aria-hidden="true" className="animate-pulse text-cream-100/50" /><p className="text-xs text-cream-100/55">Checking the forecast…</p></div>;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  if (state.status === "unavailable") {
    let t6;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="flex min-h-[84px] items-center gap-3 rounded-2xl border border-cream-100/15 bg-deep-sea-blue-950/55 px-4 py-3 backdrop-blur" {...liveProps}><Cloud size={18} aria-hidden="true" className="text-cream-100/40" /><p className="text-xs text-cream-100/70">Forecast available nearer the date.</p></div>;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  let t6;
  if ($[13] !== state.code) {
    t6 = weatherInfo(state.code);
    $[13] = state.code;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const {
    label,
    Icon
  } = t6;
  let t7;
  if ($[15] !== Icon) {
    t7 = <Icon size={30} aria-hidden="true" className="shrink-0 text-sunshine-yellow-400" />;
    $[15] = Icon;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== state.max) {
    t8 = <span className="text-2xl font-semibold leading-none text-cream-100">{state.max}°</span>;
    $[17] = state.max;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== state.min) {
    t9 = <span className="text-sm text-cream-100/70">/ {state.min}°</span>;
    $[19] = state.min;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <div className="flex items-baseline gap-1.5">{t8}{t9}</div>;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== label) {
    t11 = <div className="mt-0.5 truncate text-[11px] text-cream-100/70">{label}</div>;
    $[24] = label;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== date) {
    t12 = weatherDateLabel(date);
    $[26] = date;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12) {
    t13 = <div className="truncate text-[9px] font-semibold uppercase tracking-[0.14em] text-cream-100/70">{t12} · Cape Town</div>;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t10 || $[31] !== t11 || $[32] !== t13) {
    t14 = <div className="min-w-0 flex-1">{t10}{t11}{t13}</div>;
    $[30] = t10;
    $[31] = t11;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== state.wind) {
    t15 = <span className="text-[9px] font-semibold uppercase tracking-[0.12em] text-cream-100/60">{state.wind} km/h</span>;
    $[34] = state.wind;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const t16 = `text-[9px] font-semibold uppercase tracking-[0.12em] ${state.pop >= 50 ? "text-sunshine-yellow-400" : "text-cream-100/60"}`;
  let t17;
  if ($[36] !== state.pop || $[37] !== t16) {
    t17 = <span className={t16}>{state.pop}% rain</span>;
    $[36] = state.pop;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t15 || $[40] !== t17) {
    t18 = <div className="flex shrink-0 flex-col items-end gap-1 text-right">{t15}{t17}</div>;
    $[39] = t15;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t14 || $[43] !== t18 || $[44] !== t7) {
    t19 = <div className="flex min-h-[84px] items-center gap-3 rounded-2xl border border-cream-100/15 bg-deep-sea-blue-950/55 px-4 py-3 backdrop-blur" {...liveProps}>{t7}{t14}{t18}</div>;
    $[42] = t14;
    $[43] = t18;
    $[44] = t7;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  return t19;
}
function _temp2(r) {
  return r.json();
}
interface UpcomingDay {
  iso: string;
  weekday: string;
  day: string;
  month: string;
  tag?: string;
  weekend: boolean;
}
function upcomingDays(count = 14): UpcomingDay[] {
  const iso = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const out: UpcomingDay[] = [];
  const base = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    const dow = d.getDay();
    out.push({
      iso: iso(d),
      weekday: d.toLocaleDateString("en-ZA", {
        weekday: "short"
      }).toUpperCase(),
      day: String(d.getDate()),
      month: d.toLocaleDateString("en-ZA", {
        month: "short"
      }).toUpperCase(),
      tag: i === 0 ? "Today" : i === 1 ? "Tomorrow" : undefined,
      weekend: dow === 0 || dow === 6
    });
  }
  return out;
}
const SANITY = (id: string) => `https://cdn.sanity.io/images/wrpjy8u8/production/${id}`;
const BG = (id: string) => `${SANITY(id)}?w=1600&h=1100&q=80&fit=crop&auto=format`;
const TILE = (id: string) => `${SANITY(id)}?w=800&h=600&q=80&fit=crop&auto=format`;

// Known-valid production assets, reused as cinematic backdrops + choice tiles.
const A = {
  harbour: "9c13dee176c7dd42242d536592504ac26025cbf3-9504x6336.jpg",
  aquarium: "b095879ca3d5657d98e57b697e09013db93abab4-7740x5160.jpg",
  zeitz: "aa5bebff0af0d950bbc2cb5b89c5ebeb633d468a-5504x8256.jpg",
  sundowner: "5be0468d1a7433ceadacb76af15acf89db03fb99-5397x7556.jpg",
  market: "8f5ca10c871814448215cfe9ed10b77d094f1540-3944x7008.jpg",
  makers: "073d18ec4d08a08d6a407974994a203de0db0b6a-3084x5479.jpg",
  drinks: "25fe6d825bbde8e21191572d7ef54358337eb989-2584x3618.jpg",
  clock: "256c9af9b9bee6ca5e144781220c8fd1a635b06d-1920x1080.jpg",
  pace: "b49a847721ff76c0f4a0b6fc160ebf56ec86422f-2540x1694.jpg",
  interests: "e5246a3b795c9907e172e7ca98e454e9e83da7e5-1707x2560.jpg",
  history: "b02e388d91179de71fb47c5d5b184e5371ffc34e-4672x7008.jpg",
  foodmarket: "6d0a1fbcd0e5e626ee48f0842326682841c78226-4672x7008.jpg",
  waterfrontBridge: "040b544a88c7a9149c20232171852f3679e6cf05-3590x2393.jpg",
  foodDrink: "19fa589dec06f08d87b7655bfa0257cb01f176d2-2300x3449.jpg",
  outdoorSup: "03797a563f805b7465e2323e11a564402eb15965-4672x7008.jpg",
  sundownersPatio: "6bff4c4fd3bd229d14389c820498c98af6a2a61e-2339x3275.jpg",
  interestsClocktower: "cb8194989e4cf3a215950e056f5fa421564ca326-8295x5530.jpg",
  kayak: "ecd4c2448ee8ebac3626635fee9f9776aefbc1d4-4672x7008.jpg"
};

// Party, pace, duration and budget glyphs come from the design-system icon set (@repo/ui/icon).
const PARTY_ICONS: Record<string, ReactNode> = {
  solo: <Icon name="account-circle" className="size-8" />,
  couple: <Icon name="group" className="size-8" />,
  family: <Icon name="family" className="size-8" />,
  friends: <Icon name="groups" className="size-8" />
};
const PACE_ICONS: Record<string, ReactNode> = {
  easy: <Icon name="pace-easy" className="size-8" />,
  balanced: <Icon name="pace-balanced" className="size-8" />,
  packed: <Icon name="pace-packed" className="size-8" />
};
const DURATION_ICONS: Record<string, ReactNode> = {
  "1h": <Icon name="schedule" className="size-8" />,
  "2-3h": <Icon name="history" className="size-8" />,
  half: <Icon name="calendar-today" className="size-8" />,
  full: <Icon name="calendar-event" className="size-8" />
};
const BUDGET_ICONS: Record<string, ReactNode> = {
  light: <Icon name="pace-easy" className="size-8" />,
  mid: <Icon name="pace-balanced" className="size-8" />,
  spoil: <Icon name="pace-packed" className="size-8" />
};
const STEP_DEFS: StepDef[] = [{
  key: "date",
  eyebrow: "Step 1 of 7",
  question: "When are you visiting?",
  sub: "We'll tailor every stop to the day.",
  kind: "date",
  bg: BG(A.kayak),
  options: []
}, {
  key: "arrivalTime",
  eyebrow: "Step 2 of 7",
  question: "What time will you arrive?",
  sub: "We'll only suggest places that are open when you get there.",
  kind: "time",
  bg: BG(A.clock),
  options: []
}, {
  key: "duration",
  eyebrow: "Step 3 of 7",
  question: "How long do you have?",
  sub: "We'll size your day to fit.",
  kind: "single",
  bg: BG(A.waterfrontBridge),
  options: [{
    id: "1h",
    label: "An hour",
    sub: "A taster"
  }, {
    id: "2-3h",
    label: "2 – 3 hours",
    sub: "A proper visit"
  }, {
    id: "half",
    label: "Half a day",
    sub: "4 – 6 hours"
  }, {
    id: "full",
    label: "A full day",
    sub: "Morning to sundown"
  }]
}, {
  key: "group",
  eyebrow: "Step 4 of 7",
  question: "Who's coming along?",
  sub: "So we set the right pace.",
  kind: "single",
  bg: BG(A.aquarium),
  options: [{
    id: "solo",
    label: "Just me",
    sub: "Solo wander",
    img: TILE(A.harbour)
  }, {
    id: "couple",
    label: "A pair",
    sub: "Couples or friends",
    img: TILE(A.drinks)
  }, {
    id: "family",
    label: "Family",
    sub: "With kids in tow",
    img: TILE(A.aquarium)
  }, {
    id: "friends",
    label: "A group",
    sub: "Three or more",
    img: TILE(A.market)
  }]
}, {
  key: "pace",
  eyebrow: "Step 5 of 7",
  question: "What's your kind of day?",
  sub: "Linger, balanced, or pack it in?",
  kind: "single",
  bg: BG(A.pace),
  options: [{
    id: "easy",
    label: "Easy",
    sub: "Two or three stops, lots of lingering",
    img: TILE(A.sundowner)
  }, {
    id: "balanced",
    label: "Balanced",
    sub: "Four or five stops, room to breathe",
    img: TILE(A.makers)
  }, {
    id: "packed",
    label: "Make it count",
    sub: "See as much as we can",
    img: TILE(A.harbour)
  }]
}, {
  key: "interests",
  eyebrow: "Step 6 of 7",
  question: "What pulls you in?",
  sub: "Pick a few — we'll thread them together.",
  kind: "multi",
  bg: BG(A.interestsClocktower),
  options: [{
    id: "art",
    label: "Art & design",
    img: TILE(A.zeitz)
  }, {
    id: "food",
    label: "Food & coffee",
    img: TILE(A.foodDrink)
  }, {
    id: "shop",
    label: "Shopping & markets",
    img: TILE(A.market)
  }, {
    id: "nature",
    label: "Ocean & outdoors",
    img: TILE(A.outdoorSup)
  }, {
    id: "history",
    label: "History & heritage",
    img: TILE(A.history)
  }, {
    id: "family",
    label: "Family-friendly",
    img: TILE(A.aquarium)
  }, {
    id: "sundowner",
    label: "Sundowners",
    img: TILE(A.sundownersPatio)
  }]
}, {
  key: "budget",
  eyebrow: "Step 7 of 7",
  question: "And the budget?",
  sub: "We'll make every Rand count.",
  kind: "single",
  bg: BG(A.sundowner),
  options: [{
    id: "light",
    label: "Free & easy",
    sub: "Walks, views, browsing"
  }, {
    id: "mid",
    label: "A good middle",
    sub: "A meal, a ticket, a treat"
  }, {
    id: "spoil",
    label: "Spoil ourselves",
    sub: "The full concierge"
  }]
}];
const TOTAL = STEP_DEFS.length;

/** A step as the visitor sees it — the duration choices follow the arrival. */
function resolveStep(def: StepDef, answers: CinematicAnswers): StepDef {
  if (def.key !== "duration") return def;
  const arrival = answers.arrivalTime;
  if (!arrival || arrival === "flexible") return def;
  return {
    ...def,
    sub: `From ${formatArrival(arrival)} — we'll size your visit to fit.`,
    options: durationOptionsFor(arrival)
  };
}
function answerChips(answers: CinematicAnswers): string[] {
  const chips: string[] = [];
  for (const step of STEP_DEFS) {
    const def = resolveStep(step, answers);
    const v = answers[def.key];
    if (Array.isArray(v)) {
      if (v.length) chips.push(v.length === 1 ? def.options.find(o => o.id === v[0])?.label ?? "" : `${v.length} interests`);
    } else if (v) {
      chips.push(def.kind === "date" ? v === "flexible" ? "Flexible" : formatDateChip(v) : def.kind === "time" ? v === "flexible" ? "Any time" : `Arrive ${formatArrival(v)}` : def.options.find(o => o.id === v)?.label ?? "");
    }
  }
  return chips.filter(Boolean);
}
export function CinematicWizard(t0) {
  const $ = _c(32);
  const {
    layout,
    step,
    answers,
    setAnswers,
    onNext,
    onBack,
    pending,
    submitError
  } = t0;
  const t1 = STEP_DEFS[step];
  let t2;
  if ($[0] !== answers || $[1] !== t1) {
    t2 = resolveStep(t1, answers);
    $[0] = answers;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const def = t2;
  const isLast = step === TOTAL - 1;
  let t3;
  if ($[3] !== def.key || $[4] !== setAnswers) {
    t3 = function selectSingle(id) {
      setAnswers(a => {
        const next = {
          ...a,
          [def.key]: id
        };
        if (def.key === "arrivalTime" && next.duration && !durationOptionsFor(id).some(o => o.id === next.duration)) {
          next.duration = null;
        }
        return next;
      });
    };
    $[3] = def.key;
    $[4] = setAnswers;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const selectSingle = t3;
  let t4;
  if ($[6] !== setAnswers) {
    t4 = function toggleMulti(id_0) {
      setAnswers(a_0 => {
        const cur = a_0.interests;
        return {
          ...a_0,
          interests: cur.includes(id_0 as InterestId) ? cur.filter(x => x !== id_0) : [...cur, id_0 as InterestId]
        };
      });
    };
    $[6] = setAnswers;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const toggleMulti = t4;
  let t5;
  if ($[8] !== answers || $[9] !== def.key || $[10] !== def.kind) {
    t5 = id_1 => def.kind === "multi" ? answers.interests.includes(id_1 as InterestId) : answers[def.key] === id_1;
    $[8] = answers;
    $[9] = def.key;
    $[10] = def.kind;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const isOn = t5;
  const canSubmit = def.kind === "multi" ? answers.interests.length > 0 : def.kind === "time" ? !!answers.arrivalTime && !pastArrivals(answers.date ?? "").has(answers.arrivalTime) : !!answers[def.key];
  let t6;
  if ($[12] !== answers) {
    t6 = answerChips(answers);
    $[12] = answers;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const chips = t6;
  const t7 = answers.date ?? "";
  const t8 = answers.arrivalTime ?? "";
  let t9;
  if ($[14] !== canSubmit || $[15] !== chips || $[16] !== def || $[17] !== isLast || $[18] !== isOn || $[19] !== onBack || $[20] !== onNext || $[21] !== pending || $[22] !== selectSingle || $[23] !== step || $[24] !== submitError || $[25] !== t7 || $[26] !== t8 || $[27] !== toggleMulti) {
    t9 = {
      def,
      step,
      isLast,
      isOn,
      selectSingle,
      toggleMulti,
      canSubmit,
      chips,
      dateValue: t7,
      arrivalValue: t8,
      onNext,
      onBack,
      pending,
      submitError
    };
    $[14] = canSubmit;
    $[15] = chips;
    $[16] = def;
    $[17] = isLast;
    $[18] = isOn;
    $[19] = onBack;
    $[20] = onNext;
    $[21] = pending;
    $[22] = selectSingle;
    $[23] = step;
    $[24] = submitError;
    $[25] = t7;
    $[26] = t8;
    $[27] = toggleMulti;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const shared = t9;
  let t10;
  if ($[29] !== layout || $[30] !== shared) {
    t10 = layout === "mosaic" ? <MosaicLayout {...shared} /> : <TimelineLayout {...shared} />;
    $[29] = layout;
    $[30] = shared;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}
interface LayoutProps {
  def: StepDef;
  step: number;
  isLast: boolean;
  isOn: (id: string) => boolean;
  selectSingle: (id: string) => void;
  toggleMulti: (id: string) => void;
  canSubmit: boolean;
  chips: string[];
  dateValue: string;
  arrivalValue: string;
  onNext: () => void;
  onBack: () => void;
  pending: boolean;
  submitError: string | null;
}
function DateChoice(t0) {
  const $ = _c(24);
  const {
    dateValue,
    onPick
  } = t0;
  let t1;
  if ($[0] !== dateValue || $[1] !== onPick) {
    t1 = quickDatePicks().map(p => {
      const active = dateValue === p.iso;
      return <button key={p.label} type="button" aria-pressed={active} onClick={() => onPick(p.iso)} className={`rounded-full border px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] backdrop-blur transition-all active:scale-[0.97] ${active ? "border-sunshine-yellow-400 bg-sunshine-yellow-400 text-deep-sea-blue-950" : "border-cream-100/25 bg-deep-sea-blue-900/55 text-cream-100 hover:border-cream-100/60"}`}>{p.label}</button>;
    });
    $[0] = dateValue;
    $[1] = onPick;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = dateValue === "flexible";
  let t3;
  if ($[3] !== onPick) {
    t3 = () => onPick("flexible");
    $[3] = onPick;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = `rounded-full border px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] backdrop-blur transition-all active:scale-[0.97] ${dateValue === "flexible" ? "border-sunshine-yellow-400 bg-sunshine-yellow-400 text-deep-sea-blue-950" : "border-cream-100/25 bg-deep-sea-blue-900/55 text-cream-100 hover:border-cream-100/60"}`;
  let t5;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = <button type="button" aria-pressed={t2} onClick={t3} className={t4}>No specific date</button>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t5) {
    t6 = <div className="flex flex-wrap gap-2">{t1}{t5}</div>;
    $[9] = t1;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <label htmlFor="concierge-visit-date" className="sr-only">Or choose a specific date for your visit</label>;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== onPick) {
    t8 = e => {
      if (e.target.value) {
        onPick(e.target.value);
      }
    };
    $[13] = onPick;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== dateValue || $[16] !== t8) {
    t9 = <input id="concierge-visit-date" type="date" value={dateValue} onChange={t8} className="w-full max-w-[16rem] rounded-full border border-cream-100/25 bg-deep-sea-blue-900/55 px-4 py-2.5 text-sm text-cream-100 backdrop-blur [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-datetime-edit]:p-0 focus-visible:border-sunshine-yellow-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sunshine-yellow-400" />;
    $[15] = dateValue;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== dateValue) {
    t10 = <WeatherWidget key={dateValue} date={dateValue} />;
    $[18] = dateValue;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t6 || $[22] !== t9) {
    t11 = <div className="flex max-w-sm flex-col gap-3">{t6}{t7}{t9}{t10}</div>;
    $[20] = t10;
    $[21] = t6;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}

// v0's mosaic date selector — a horizontal day-tile rail with node dots.
function DateTimeline(t0) {
  const $ = _c(59);
  const {
    dateValue,
    onPick,
    onHover
  } = t0;
  const inputRef = useRef(null);
  let customLabel;
  let onRail;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== dateValue || $[1] !== onHover || $[2] !== onPick) {
    const days = upcomingDays(14);
    let t7;
    if ($[11] !== onHover) {
      t7 = iso => onHover ? {
        onMouseEnter: () => onHover(iso),
        onMouseLeave: () => onHover(""),
        onFocus: () => onHover(iso),
        onBlur: () => onHover("")
      } : {};
      $[11] = onHover;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    const hoverProps = t7;
    let t8;
    if ($[13] !== dateValue) {
      t8 = d => d.iso === dateValue;
      $[13] = dateValue;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    onRail = days.some(t8);
    customLabel = dateValue && !onRail ? formatDateChip(dateValue) : "Another date";
    t5 = "flex flex-col gap-5";
    let t9;
    if ($[15] !== dateValue || $[16] !== hoverProps || $[17] !== onPick) {
      t9 = quickDatePicks().map(p => {
        const active = dateValue === p.iso;
        return <button key={p.label} type="button" onClick={() => onPick(p.iso)} {...hoverProps(p.iso)} className={`rounded-full border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] backdrop-blur transition-all active:scale-[0.97] ${active ? "border-sunshine-yellow-400 bg-sunshine-yellow-400 text-deep-sea-blue-950" : "border-cream-100/40 bg-deep-sea-blue-900/55 text-cream-100 hover:border-cream-100/50"}`}>{p.label}</button>;
      });
      $[15] = dateValue;
      $[16] = hoverProps;
      $[17] = onPick;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] !== onPick) {
      t10 = () => onPick("flexible");
      $[19] = onPick;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    const t11 = `rounded-full border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] backdrop-blur transition-all active:scale-[0.97] ${dateValue === "flexible" ? "border-sunshine-yellow-400 bg-sunshine-yellow-400 text-deep-sea-blue-950" : "border-cream-100/40 bg-deep-sea-blue-900/55 text-cream-100 hover:border-cream-100/50"}`;
    let t12;
    if ($[21] !== t10 || $[22] !== t11) {
      t12 = <button type="button" onClick={t10} className={t11}>No specific date</button>;
      $[21] = t10;
      $[22] = t11;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    if ($[24] !== t12 || $[25] !== t9) {
      t6 = <div className="flex flex-wrap gap-2">{t9}{t12}</div>;
      $[24] = t12;
      $[25] = t9;
      $[26] = t6;
    } else {
      t6 = $[26];
    }
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <p className="mb-4 text-[10px] font-semibold uppercase tracking-[0.28em] text-cream-100/55">Choose your day</p>;
      $[27] = t4;
    } else {
      t4 = $[27];
    }
    t3 = "relative";
    t1 = "scrollbar-none -mx-1 flex gap-3 overflow-x-auto px-1 pb-3";
    let t13;
    if ($[28] !== dateValue || $[29] !== hoverProps || $[30] !== onPick) {
      t13 = d_0 => {
        const on = dateValue === d_0.iso;
        return <button key={d_0.iso} type="button" aria-pressed={on} aria-label={`${weatherDateLabel(d_0.iso)}${d_0.tag ? ` (${d_0.tag})` : ""}`} onClick={() => onPick(d_0.iso)} {...hoverProps(d_0.iso)} className="relative flex w-16 flex-shrink-0 flex-col items-center rounded-xl transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sunshine-yellow-400 active:scale-[0.96]"><span className={`mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em] ${on ? "text-sunshine-yellow-400" : "text-cream-100/55"}`}>{d_0.weekday}</span><span className={`z-10 mb-3 h-2.5 w-2.5 rounded-full transition-all ${on ? "bg-sunshine-yellow-400 ring-4 ring-sunshine-yellow-400/20" : "bg-cream-100/30"}`} /><span className={`flex w-full flex-col items-center justify-center rounded-xl border py-2.5 backdrop-blur transition-all ${on ? "border-sunshine-yellow-400 bg-sunshine-yellow-400" : d_0.weekend ? "border-sunshine-yellow-400/30 bg-deep-sea-blue-900/55" : "border-cream-100/14 bg-deep-sea-blue-900/55"}`}><span className={`text-2xl font-medium leading-none ${on ? "text-deep-sea-blue-950" : "text-cream-100"}`}>{d_0.day}</span><span className={`mt-1 text-[9px] font-semibold uppercase tracking-[0.14em] ${on ? "text-deep-sea-blue-950/70" : "text-cream-100/70"}`}>{d_0.month}</span></span>{d_0.tag ? <span className={`mt-1.5 text-[8px] font-semibold uppercase tracking-[0.16em] ${on ? "text-sunshine-yellow-400" : "text-cream-100/70"}`}>{d_0.tag}</span> : null}</button>;
      };
      $[28] = dateValue;
      $[29] = hoverProps;
      $[30] = onPick;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    t2 = days.map(t13);
    $[0] = dateValue;
    $[1] = onHover;
    $[2] = onPick;
    $[3] = customLabel;
    $[4] = onRail;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    customLabel = $[3];
    onRail = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[32] !== t1 || $[33] !== t2) {
    t7 = <div className={t1}>{t2}</div>;
    $[32] = t1;
    $[33] = t2;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  let t8;
  if ($[35] !== t3 || $[36] !== t7) {
    t8 = <div className={t3}>{t7}</div>;
    $[35] = t3;
    $[36] = t7;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  let t9;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const el = inputRef.current;
      if (!el) {
        return;
      }
      if (typeof el.showPicker === "function") {
        el.showPicker();
      } else {
        el.focus();
      }
    };
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  const t10 = `relative inline-flex items-center gap-2 rounded-full border px-4 py-2.5 backdrop-blur transition-all active:scale-[0.97] ${dateValue && !onRail ? "border-sunshine-yellow-400 bg-sunshine-yellow-400/15" : "border-cream-100/40 bg-deep-sea-blue-900/55 hover:border-cream-100/50"}`;
  const t11 = `text-[10px] font-semibold uppercase tracking-[0.16em] ${dateValue && !onRail ? "text-sunshine-yellow-400" : "text-cream-100"}`;
  let t12;
  if ($[39] !== customLabel || $[40] !== t11) {
    t12 = <span className={t11}>{customLabel}</span>;
    $[39] = customLabel;
    $[40] = t11;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  const t13 = dateValue && !onRail ? dateValue : "";
  let t14;
  if ($[42] !== onPick) {
    t14 = e => {
      if (e.target.value) {
        onPick(e.target.value);
      }
    };
    $[42] = onPick;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== t13 || $[45] !== t14) {
    t15 = <input ref={inputRef} type="date" value={t13} onChange={t14} className="pointer-events-none absolute bottom-0 left-4 h-px w-px opacity-0" tabIndex={-1} aria-hidden="true" />;
    $[44] = t13;
    $[45] = t14;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== t10 || $[48] !== t12 || $[49] !== t15) {
    t16 = <div className="mt-5"><button type="button" onClick={t9} className={t10}>{t12}{t15}</button></div>;
    $[47] = t10;
    $[48] = t12;
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== t16 || $[52] !== t4 || $[53] !== t8) {
    t17 = <div>{t4}{t8}{t16}</div>;
    $[51] = t16;
    $[52] = t4;
    $[53] = t8;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] !== t17 || $[56] !== t5 || $[57] !== t6) {
    t18 = <div className={t5}>{t6}{t17}</div>;
    $[55] = t17;
    $[56] = t5;
    $[57] = t6;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  return t18;
}
function ProgressDots(t0) {
  const $ = _c(7);
  const {
    step
  } = t0;
  const t1 = step + 1;
  const t2 = `Step ${step + 1} of ${TOTAL}`;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = Array.from({
      length: TOTAL
    });
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== step) {
    t4 = t3.map((_, i) => <span key={i} aria-hidden="true" className={`h-1.5 rounded-full transition-all duration-500 ${i <= step ? "bg-sunshine-yellow-400" : "bg-cream-100/20"}`} style={{
      width: i === step ? 22 : 6
    }} />);
    $[1] = step;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t4) {
    t5 = <div role="progressbar" aria-valuenow={t1} aria-valuemin={1} aria-valuemax={TOTAL} aria-label={t2} className="flex items-center gap-2">{t4}</div>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}
function Breadcrumbs(t0) {
  const $ = _c(4);
  const {
    chips
  } = t0;
  if (!chips.length) {
    return null;
  }
  let t1;
  if ($[0] !== chips) {
    t1 = chips.map(_temp3);
    $[0] = chips;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="flex flex-wrap gap-1.5">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp3(c) {
  return <span key={c} className="rounded-full border border-sunshine-yellow-400/25 bg-sunshine-yellow-400/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-sunshine-yellow-400">{c}</span>;
}
function SubmitButton(t0) {
  const $ = _c(17);
  const {
    pending,
    disabled,
    onClick,
    children
  } = t0;
  const blocked = disabled || pending;
  let t1;
  if ($[0] !== blocked || $[1] !== onClick) {
    t1 = () => {
      if (blocked) {
        return;
      }
      onClick();
    };
    $[0] = blocked;
    $[1] = onClick;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = disabled ? "wizard-submit-hint" : undefined;
  const t3 = pending ? "Planning your day\u2026" : children;
  let t4;
  if ($[3] !== blocked || $[4] !== pending || $[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = <button type="button" onClick={t1} aria-disabled={blocked} aria-busy={pending} aria-describedby={t2} className="inline-flex items-center gap-2 rounded-2xl bg-sunshine-yellow-400 px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-deep-sea-blue-950 transition-all hover:brightness-105 aria-disabled:cursor-not-allowed aria-disabled:opacity-35">{t3}</button>;
    $[3] = blocked;
    $[4] = pending;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== disabled) {
    t5 = disabled ? <span id="wizard-submit-hint" className="sr-only">Choose an option to continue.</span> : null;
    $[9] = disabled;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = pending ? "Planning your day, please wait." : "";
  let t7;
  if ($[11] !== t6) {
    t7 = <span className="sr-only" aria-live="polite">{t6}</span>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t4 || $[14] !== t5 || $[15] !== t7) {
    t8 = <>{t4}{t5}{t7}</>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
function BackButton(t0) {
  const $ = _c(2);
  const {
    onBack
  } = t0;
  let t1;
  if ($[0] !== onBack) {
    t1 = <button type="button" onClick={onBack} className="inline-flex items-center rounded-2xl border border-cream-100/25 px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-cream-100/70 transition-colors hover:border-cream-100/60 hover:text-cream-100">← Back</button>;
    $[0] = onBack;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/* -------------------------------------------------------------------------- */
/* Timeline layout — cinematic full-bleed, content anchored bottom            */
/* -------------------------------------------------------------------------- */

/**
 * Moves focus to the new step's heading when the step changes, so keyboard and
 * screen-reader users land on the question instead of being dropped at <body>
 * when the previous step unmounts. Skips the initial mount.
 */
function useStepHeadingFocus(step) {
  const $ = _c(3);
  const headingRef = useRef(null);
  const mounted = useRef(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (!mounted.current) {
        mounted.current = true;
        return;
      }
      headingRef.current?.focus({
        preventScroll: true
      });
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== step) {
    t1 = [step];
    $[1] = step;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useEffect(t0, t1);
  return headingRef;
}
function TimelineLayout(t0) {
  const $ = _c(56);
  const {
    def,
    step,
    isLast,
    isOn,
    selectSingle,
    toggleMulti,
    canSubmit,
    chips,
    dateValue,
    arrivalValue,
    onNext,
    onBack,
    pending,
    submitError
  } = t0;
  const headingRef = useStepHeadingFocus(step);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      filter: "brightness(0.62)"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== def.bg || $[2] !== def.key) {
    t2 = <Image key={def.key} src={def.bg} alt="" fill={true} priority={true} className="object-cover ken-burns" sizes="100vw" style={t1} />;
    $[1] = def.bg;
    $[2] = def.key;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="absolute inset-0 bg-gradient-to-b from-deep-sea-blue-950/40 via-deep-sea-blue-950/30 to-deep-sea-blue-950" />;
    t4 = <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-deep-sea-blue-950/70 to-transparent" />;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2) {
    t5 = <div aria-hidden={true} className="absolute inset-0 -z-10">{t2}{t3}{t4}</div>;
    $[6] = t2;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== step) {
    t6 = <div className="relative z-10 mx-auto flex w-full max-w-5xl items-center justify-start px-6 pt-7 sm:px-10"><ProgressDots step={step} /></div>;
    $[8] = step;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== chips) {
    t7 = <div className="relative z-10 mx-auto mt-5 min-h-[28px] w-full max-w-5xl px-6 sm:px-10"><Breadcrumbs chips={chips} /></div>;
    $[10] = chips;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = `c-${def.key}`;
  let t9;
  if ($[12] !== def.eyebrow) {
    t9 = <p className="rise-in rise-in-1 mb-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-sunshine-yellow-400">{def.eyebrow}</p>;
    $[12] = def.eyebrow;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== def.question || $[15] !== headingRef) {
    t10 = <h2 ref={headingRef} tabIndex={-1} className="rise-in rise-in-2 typography-mobile-heading-h1-voyage desktop:typography-desktop-heading-h1-voyage mb-3 max-w-3xl text-balance text-cream-100 focus:outline-none">{def.question}</h2>;
    $[14] = def.question;
    $[15] = headingRef;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== def.sub) {
    t11 = <p className="rise-in rise-in-3 mb-8 max-w-sm text-sm leading-relaxed text-cream-100/65">{def.sub}</p>;
    $[17] = def.sub;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== arrivalValue || $[20] !== dateValue || $[21] !== def.kind || $[22] !== def.options || $[23] !== def.question || $[24] !== isOn || $[25] !== selectSingle || $[26] !== toggleMulti) {
    t12 = def.kind === "date" ? <div className="rise-in rise-in-4"><DateChoice dateValue={dateValue} onPick={selectSingle} /></div> : def.kind === "time" ? <div className="rise-in rise-in-4"><ArrivalTimeChoice dateValue={dateValue} value={arrivalValue} onPick={selectSingle} /></div> : def.kind === "single" ? <div role="group" aria-label={def.question} className="rise-in rise-in-4 grid max-w-xl grid-cols-2 gap-3">{def.options.map(opt => <button key={opt.id} type="button" aria-pressed={isOn(opt.id)} onClick={() => selectSingle(opt.id)} className={`relative rounded-2xl border p-4 text-left backdrop-blur transition-all active:scale-[0.97] ${isOn(opt.id) ? "border-sunshine-yellow-400 bg-sunshine-yellow-400/15" : "border-cream-100/10 bg-deep-sea-blue-900/55 hover:border-cream-100/40"}`}><div className={`typography-component-md-medium ${isOn(opt.id) ? "text-sunshine-yellow-400" : "text-cream-100"}`}>{opt.label}</div>{opt.sub ? <div className="mt-0.5 text-[11px] text-cream-100/70">{opt.sub}</div> : null}</button>)}</div> : <div role="group" aria-label={def.question} className="rise-in rise-in-4 flex max-w-lg flex-wrap gap-2.5">{def.options.map(opt_0 => <button key={opt_0.id} type="button" aria-pressed={isOn(opt_0.id)} onClick={() => toggleMulti(opt_0.id)} className={`rounded-full border px-5 py-3 text-sm font-medium backdrop-blur transition-all active:scale-[0.96] ${isOn(opt_0.id) ? "border-sunshine-yellow-400 bg-sunshine-yellow-400/15 text-sunshine-yellow-400" : "border-cream-100/15 bg-deep-sea-blue-900/55 text-cream-100 hover:border-cream-100/40"}`}>{opt_0.label}</button>)}</div>;
    $[19] = arrivalValue;
    $[20] = dateValue;
    $[21] = def.kind;
    $[22] = def.options;
    $[23] = def.question;
    $[24] = isOn;
    $[25] = selectSingle;
    $[26] = toggleMulti;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== submitError) {
    t13 = submitError ? <p className="text-sm text-error-400">{submitError}</p> : null;
    $[28] = submitError;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13) {
    t14 = <div role="alert" aria-live="assertive" className="mt-4 min-h-[1.25rem]">{t13}</div>;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== onBack || $[33] !== step) {
    t15 = step > 0 ? <BackButton onBack={onBack} /> : null;
    $[32] = onBack;
    $[33] = step;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const t16 = !canSubmit;
  const t17 = isLast ? "Build my day \u2192" : "Continue \u2192";
  let t18;
  if ($[35] !== onNext || $[36] !== pending || $[37] !== t16 || $[38] !== t17) {
    t18 = <SubmitButton pending={pending} disabled={t16} onClick={onNext}>{t17}</SubmitButton>;
    $[35] = onNext;
    $[36] = pending;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t15 || $[41] !== t18) {
    t19 = <div className="mt-7 flex items-center gap-3">{t15}{t18}</div>;
    $[40] = t15;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t10 || $[44] !== t11 || $[45] !== t12 || $[46] !== t14 || $[47] !== t19 || $[48] !== t8 || $[49] !== t9) {
    t20 = <div key={t8} className="relative z-10 mx-auto flex min-h-[600px] w-full max-w-5xl flex-col justify-end px-6 pb-12 pt-24 sm:px-10">{t9}{t10}{t11}{t12}{t14}{t19}</div>;
    $[43] = t10;
    $[44] = t11;
    $[45] = t12;
    $[46] = t14;
    $[47] = t19;
    $[48] = t8;
    $[49] = t9;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== t20 || $[52] !== t5 || $[53] !== t6 || $[54] !== t7) {
    t21 = <div className="relative isolate min-h-[calc(100dvh+8px)] overflow-hidden bg-deep-sea-blue-950 tablet:min-h-[calc(100dvh+32px)] desktop-sm:min-h-[calc(100dvh+24px)]">{t5}{t6}{t7}{t20}</div>;
    $[51] = t20;
    $[52] = t5;
    $[53] = t6;
    $[54] = t7;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  return t21;
}

/* -------------------------------------------------------------------------- */
/* Mosaic layout — the choices are photographs                                */
/* -------------------------------------------------------------------------- */

function MosaicLayout(t0) {
  const $ = _c(63);
  const {
    def,
    step,
    isLast,
    isOn,
    selectSingle,
    toggleMulti,
    canSubmit,
    chips,
    dateValue,
    arrivalValue,
    onNext,
    onBack,
    pending,
    submitError
  } = t0;
  const headingRef = useStepHeadingFocus(step);
  const [hoverDate, setHoverDate] = useState("");
  let t1;
  if ($[0] !== dateValue || $[1] !== hoverDate) {
    t1 = hoverDate || (dateValue && dateValue !== "flexible" ? dateValue : upcomingDays(1)[0]?.iso ?? "");
    $[0] = dateValue;
    $[1] = hoverDate;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const previewIso = t1;
  const tileIcons = def.key === "group" ? PARTY_ICONS : def.key === "pace" ? PACE_ICONS : def.key === "duration" ? DURATION_ICONS : def.key === "budget" ? BUDGET_ICONS : null;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      filter: "brightness(0.78)"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== def.bg || $[5] !== def.key) {
    t3 = <Image key={def.key} src={def.bg} alt="" fill={true} priority={true} className="object-cover ken-burns" sizes="100vw" style={t2} />;
    $[4] = def.bg;
    $[5] = def.key;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="absolute inset-0 bg-gradient-to-b from-deep-sea-blue-950/35 via-deep-sea-blue-950/55 to-deep-sea-blue-950" />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3) {
    t5 = <div aria-hidden={true} className="absolute inset-0 -z-10">{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== step) {
    t6 = <div className="flex mx-auto w-full max-w-6xl items-center justify-start pt-7 pb-5 screen-padding"><ProgressDots step={step} /></div>;
    $[10] = step;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = `t-${def.key}`;
  let t8;
  if ($[12] !== def.eyebrow) {
    t8 = <p className="rise-in rise-in-1 mb-3 text-[11px] font-semibold uppercase tracking-[0.34em] text-sunshine-yellow-400">{def.eyebrow}</p>;
    $[12] = def.eyebrow;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== def.question || $[15] !== headingRef) {
    t9 = <h2 ref={headingRef} tabIndex={-1} className="rise-in rise-in-2 typography-mobile-heading-h1-voyage desktop:typography-desktop-heading-h1-voyage text-balance text-cream-100 focus:outline-none">{def.question}</h2>;
    $[14] = def.question;
    $[15] = headingRef;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== def.sub) {
    t10 = <p className="rise-in rise-in-3 mt-3 max-w-md text-sm leading-relaxed text-cream-100/55">{def.sub}</p>;
    $[17] = def.sub;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t11 = <div key={t7} className="mx-auto w-full max-w-6xl pb-6 screen-padding">{t8}{t9}{t10}</div>;
    $[19] = t10;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = `g-${def.key}`;
  let t13;
  if ($[24] !== arrivalValue || $[25] !== dateValue || $[26] !== def.kind || $[27] !== def.options || $[28] !== def.question || $[29] !== isOn || $[30] !== previewIso || $[31] !== selectSingle || $[32] !== tileIcons || $[33] !== toggleMulti) {
    t13 = def.kind === "date" ? <div className="flex max-w-md flex-col gap-5"><WeatherWidget key={previewIso} date={previewIso} /><DateTimeline dateValue={dateValue} onPick={selectSingle} onHover={setHoverDate} /></div> : def.kind === "time" ? <ArrivalTimeChoice dateValue={dateValue} value={arrivalValue} onPick={selectSingle} /> : <div role="group" aria-label={def.question} className={`grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4 ${tileIcons ? "auto-rows-fr" : ""}`}>{def.options.map(opt => {
        const on = isOn(opt.id);
        const icon = tileIcons?.[opt.id];
        return <button key={opt.id} type="button" aria-pressed={on} onClick={() => def.kind === "multi" ? toggleMulti(opt.id) : selectSingle(opt.id)} className={`group relative overflow-hidden rounded-2xl border transition-all active:scale-[0.98] ${icon ? "grid before:pt-[75%] before:content-[''] before:[grid-area:1/1]" : ""} ${on ? "border-sunshine-yellow-400 ring-2 ring-sunshine-yellow-400" : "border-cream-100/10"}`} style={icon ? undefined : {
          aspectRatio: "4 / 3"
        }}>{icon ? <><div className="absolute inset-0 bg-deep-sea-blue-900/45" /><div className="relative flex flex-col justify-between gap-4 p-5 [grid-area:1/1]"><span aria-hidden="true" className={`flex h-9 items-center transition-colors [&>svg]:h-full [&>svg]:w-auto ${on ? "text-sunshine-yellow-400" : "text-cream-100/65 group-hover:text-cream-100"}`}>{icon}</span><div className="text-left"><div className={`typography-mobile-heading-subtitle-1 desktop:typography-desktop-heading-subtitle-1 ${on ? "text-sunshine-yellow-400" : "text-cream-100"}`}>{opt.label}</div>{opt.sub ? <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-cream-100/60">{opt.sub}</div> : null}</div></div></> : <>{opt.img ? <Image src={opt.img} alt="" fill={true} className={`object-cover transition-transform duration-700 group-hover:scale-110 ${on ? "" : "brightness-[0.55]"}`} sizes="(max-width: 1024px) 50vw, 320px" /> : null}<div className="absolute inset-0 bg-gradient-to-t from-deep-sea-blue-950/90 via-deep-sea-blue-950/10 to-transparent" />{def.kind === "multi" ? <span aria-hidden="true" className={`absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full border backdrop-blur ${on ? "border-sunshine-yellow-400 bg-sunshine-yellow-400 text-deep-sea-blue-950" : "border-cream-100/40 bg-deep-sea-blue-950/50"}`}>{on ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg> : null}</span> : null}<div className="absolute inset-x-0 bottom-0 p-4 text-left"><div className={`typography-mobile-heading-subtitle-1 desktop:typography-desktop-heading-subtitle-1 ${on ? "text-sunshine-yellow-400" : "text-cream-100"}`}>{opt.label}</div>{opt.sub ? <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-cream-100/60">{opt.sub}</div> : null}</div></>}</button>;
      })}</div>;
    $[24] = arrivalValue;
    $[25] = dateValue;
    $[26] = def.kind;
    $[27] = def.options;
    $[28] = def.question;
    $[29] = isOn;
    $[30] = previewIso;
    $[31] = selectSingle;
    $[32] = tileIcons;
    $[33] = toggleMulti;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== submitError) {
    t14 = submitError ? <p className="text-sm text-error-400">{submitError}</p> : null;
    $[35] = submitError;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t14) {
    t15 = <div role="alert" aria-live="assertive" className="mt-4 min-h-[1.25rem]">{t14}</div>;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== onBack || $[40] !== step) {
    t16 = step > 0 ? <BackButton onBack={onBack} /> : null;
    $[39] = onBack;
    $[40] = step;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const t17 = !canSubmit;
  const t18 = isLast ? "Build my day \u2192" : "Continue \u2192";
  let t19;
  if ($[42] !== onNext || $[43] !== pending || $[44] !== t17 || $[45] !== t18) {
    t19 = <SubmitButton pending={pending} disabled={t17} onClick={onNext}>{t18}</SubmitButton>;
    $[42] = onNext;
    $[43] = pending;
    $[44] = t17;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t16 || $[48] !== t19) {
    t20 = <div className="mt-7 flex items-center gap-3">{t16}{t19}</div>;
    $[47] = t16;
    $[48] = t19;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== t12 || $[51] !== t13 || $[52] !== t15 || $[53] !== t20) {
    t21 = <div key={t12} className="rise-in rise-in-4 mx-auto w-full max-w-6xl pb-8 screen-padding">{t13}{t15}{t20}</div>;
    $[50] = t12;
    $[51] = t13;
    $[52] = t15;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== chips) {
    t22 = <div className="flex mx-auto w-full max-w-6xl flex-wrap items-center gap-4 pb-8 screen-padding"><Breadcrumbs chips={chips} /></div>;
    $[55] = chips;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== t11 || $[58] !== t21 || $[59] !== t22 || $[60] !== t5 || $[61] !== t6) {
    t23 = <div data-planner-hero={true} className="relative isolate flex min-h-[calc(100dvh+8px)] flex-col justify-center overflow-hidden bg-deep-sea-blue-950 tablet:min-h-[calc(100dvh+32px)] desktop-sm:min-h-[calc(100dvh+24px)]">{t5}{t6}{t11}{t21}{t22}</div>;
    $[57] = t11;
    $[58] = t21;
    $[59] = t22;
    $[60] = t5;
    $[61] = t6;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  return t23;
}